import React, { useMemo, useState } from 'react';
import {
  Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import Panel from '../crt/Panel';
import { seoSerpAPI } from '../../services/api';

type Competitor = {
  domain: string;
  display_name: string;
  is_us: boolean;
  color: string;
  category: 'us' | 'podcast_competitor' | 'aggregator';
};
type Keyword = {
  keyword: string;
  our_show: string;
  category: string;
};

/**
 * Tiny inline rank sparkline. Rank is inverted (1 = best at top), so we
 * map position→y with smaller position higher up. Pure SVG, no recharts
 * instance per row (cheap for ~20 rows). `pts` is [{d,p}] ascending date.
 */
const RankSparkline: React.FC<{ pts: { d: string; p: number }[] }> = ({ pts }) => {
  const W = 90, H = 22, pad = 2;
  if (!pts || pts.length === 0) return <span style={{ opacity: 0.4 }}>—</span>;
  if (pts.length === 1) {
    return (
      <svg width={W} height={H}>
        <circle cx={W / 2} cy={H / 2} r={2.5} fill="#00ff41" />
      </svg>
    );
  }
  const maxP = Math.max(...pts.map((x) => x.p), 10);
  const minP = Math.min(...pts.map((x) => x.p), 1);
  const span = Math.max(1, maxP - minP);
  const xStep = (W - pad * 2) / (pts.length - 1);
  // smaller position (better) → higher on screen (smaller y)
  const y = (p: number) => pad + ((p - minP) / span) * (H - pad * 2);
  const d = pts
    .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * xStep} ${y(pt.p)}`)
    .join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={W} height={H} style={{ verticalAlign: 'middle' }}>
      <path d={d} fill="none" stroke="#00ff41" strokeWidth={1.2} />
      <circle cx={pad + (pts.length - 1) * xStep} cy={y(last.p)} r={2} fill="#00ff41" />
    </svg>
  );
};

type OtherDomain = {
  domain: string;
  latest_position: number;
  best_position: number;
  avg_position: number | null;
  delta: number | null;
  days_seen: number;
  first_seen: string;
  last_seen: string;
  url: string;
  title: string;
  history: { d: string; p: number }[];
};

/**
 * One keyword dropdown drives both line charts.
 * Chart 1: us + podcast competitors  (head-to-head with other shows)
 * Chart 2: us + aggregators          (vs Reddit/Goodpods/Wikipedia/etc.)
 *
 * Each tracked domain is a series. Recharts' connectNulls keeps a line
 * going across days where the domain didn't appear in top-10. Y-axis
 * is inverted (1 at top, 10 at bottom) so "higher" visually means
 * "ranked better".
 */
const CompetitorSerpPanel: React.FC = () => {
  const [selectedKw, setSelectedKw] = useState<string>('');
  const [days, setDays] = useState<number>(180);

  const { data: meta, isLoading: metaLoading } = useQuery({
    queryKey: ['seo-serp-keywords'],
    queryFn: seoSerpAPI.keywords,
    staleTime: 10 * 60_000,
  });

  React.useEffect(() => {
    if (meta?.keywords?.length && !selectedKw) {
      setSelectedKw(meta.keywords[0].keyword);
    }
  }, [meta, selectedKw]);

  const { data: podcastTrends } = useQuery({
    queryKey: ['seo-serp-rankings', selectedKw, days, 'podcast_competitor'],
    queryFn: () => seoSerpAPI.rankings(selectedKw, days, 'podcast_competitor'),
    enabled: !!selectedKw,
    staleTime: 5 * 60_000,
  });
  const { data: aggregatorTrends } = useQuery({
    queryKey: ['seo-serp-rankings', selectedKw, days, 'aggregator'],
    queryFn: () => seoSerpAPI.rankings(selectedKw, days, 'aggregator'),
    enabled: !!selectedKw,
    staleTime: 5 * 60_000,
  });
  // Always pull "us" series and merge into both charts
  const { data: usTrends } = useQuery({
    queryKey: ['seo-serp-rankings', selectedKw, days, 'us'],
    queryFn: () => seoSerpAPI.rankings(selectedKw, days, 'us'),
    enabled: !!selectedKw,
    staleTime: 5 * 60_000,
  });

  const { data: otherDomains } = useQuery({
    queryKey: ['seo-serp-other', selectedKw, days],
    queryFn: () => seoSerpAPI.otherDomains(selectedKw, days),
    enabled: !!selectedKw,
    staleTime: 5 * 60_000,
  });

  const competitors: Competitor[] = meta?.competitors || [];
  const keywords:    Keyword[]    = meta?.keywords    || [];
  const us         = competitors.filter((c) => c.category === 'us');
  const podcasters = competitors.filter((c) => c.category === 'podcast_competitor');
  const aggregators = competitors.filter((c) => c.category === 'aggregator');

  /** Merge per-category series with the 'us' series so our line is on every chart. */
  const merged = (catSeries?: any[], usSeries?: any[]) => {
    const byDate: Record<string, any> = {};
    for (const s of (catSeries || [])) byDate[s.date] = { ...byDate[s.date], ...s };
    for (const s of (usSeries  || [])) byDate[s.date] = { ...byDate[s.date], ...s };
    return Object.values(byDate).sort((a: any, b: any) => a.date.localeCompare(b.date));
  };
  const podcastSeries    = useMemo(() => merged(podcastTrends?.series, usTrends?.series), [podcastTrends, usTrends]);
  const aggregatorSeries = useMemo(() => merged(aggregatorTrends?.series, usTrends?.series), [aggregatorTrends, usTrends]);

  if (metaLoading) {
    return <Panel label="COMPETITOR SERP RANKINGS"><Typography variant="caption">loading…</Typography></Panel>;
  }
  if (!meta?.last_snapshot_date) {
    return (
      <Panel label="COMPETITOR SERP RANKINGS">
        <Typography variant="body2" color="text.secondary">
          No SERP data yet. Weekly cron runs Mondays 04:30 UTC. Manual:{' '}
          <code>python -m etl.cli.serper_serp_run --apply</code>
        </Typography>
      </Panel>
    );
  }

  const ChartFor = ({ series, lines, title }: { series: any[]; lines: Competitor[]; title: string }) => (
    <Panel label={title}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid stroke="#1e3a1e" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#5aaa5a" tick={{ fontSize: 11 }} />
              <YAxis
                reversed
                domain={[1, 30]}
                ticks={[1, 3, 5, 10, 15, 20, 25, 30]}
                allowDataOverflow
                stroke="#5aaa5a"
                tick={{ fontSize: 11 }}
                label={{ value: 'rank', angle: -90, position: 'insideLeft', fill: '#5aaa5a', fontSize: 11 }}
              />
              <Tooltip contentStyle={{ backgroundColor: '#0f0f0f', border: '1px solid #1e3a1e' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {lines.map((c) => (
                <Line
                  key={c.domain}
                  type="monotone"
                  dataKey={c.domain}
                  name={c.display_name}
                  stroke={c.color}
                  strokeWidth={c.is_us ? 3 : 1.5}
                  connectNulls
                  dot={{ r: c.is_us ? 4 : 2 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
        {series.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No data points for this keyword in this category.
          </Typography>
        )}
      </Box>
    </Panel>
  );

  return (
    <Stack spacing={3}>
      <Panel label="SERP TRACKING — KEYWORD SELECTOR">
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Weekly Google rank tracking via Serper.dev. Top ~30 per keyword.
            Y-axis inverted — closer to top = ranking better.
            Last snapshot: <strong>{new Date(meta.last_snapshot_date).toLocaleDateString()}</strong>.
            Approx credits used: <strong>{meta.approx_credits_used}</strong> / 2,500.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 360 }}>
              <InputLabel>Keyword</InputLabel>
              <Select
                label="Keyword"
                value={selectedKw}
                onChange={(e) => setSelectedKw(e.target.value)}
              >
                {keywords.map((kw) => (
                  <MenuItem key={kw.keyword} value={kw.keyword}>
                    [{kw.category}] {kw.keyword}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Window</InputLabel>
              <Select label="Window" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={180}>Last 180 days</MenuItem>
                <MenuItem value={365}>Last year</MenuItem>
                <MenuItem value={730}>Last 2 years</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Panel>

      <ChartFor
        series={podcastSeries}
        lines={[...us, ...podcasters]}
        title="VS PODCAST COMPETITORS — HEAD-TO-HEAD"
      />

      <ChartFor
        series={aggregatorSeries}
        lines={[...us, ...aggregators]}
        title="VS AGGREGATORS — REDDIT / GOODPODS / WIKIPEDIA / ETC."
      />

      <Panel label="UNTRACKED DOMAINS RANKING FOR THIS KEYWORD">
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Domains ranking for <code>{selectedKw}</code> that AREN'T tracked competitors yet —
            one row per domain over the last {otherDomains?.days ?? days} days
            ({otherDomains?.total_days ?? 0} snapshots). Sorted by who ranks best right now.
            Consistently high + untracked? Add it as a tracked aggregator.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>DOMAIN</TableCell>
                  <TableCell align="center">NOW</TableCell>
                  <TableCell align="center">Δ</TableCell>
                  <TableCell align="center">BEST</TableCell>
                  <TableCell align="center">AVG</TableCell>
                  <TableCell align="center">SEEN</TableCell>
                  <TableCell align="center">TREND</TableCell>
                  <TableCell>LATEST TITLE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {((otherDomains?.domains as OtherDomain[]) || []).map((r, i) => {
                  const totalDays = otherDomains?.total_days ?? 0;
                  const up = (r.delta ?? 0) > 0;
                  const down = (r.delta ?? 0) < 0;
                  return (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography
                          component="a"
                          href={r.url}
                          target="_blank"
                          rel="noopener"
                          variant="body2"
                          fontFamily="monospace"
                          fontSize={12}
                          sx={{ color: '#00ff41', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {r.domain}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={r.latest_position}
                          size="small"
                          variant={r.latest_position <= 3 ? 'filled' : 'outlined'}
                          color={r.latest_position <= 3 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {r.delta == null || r.delta === 0 ? (
                          <Typography variant="caption" sx={{ opacity: 0.4 }}>—</Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: up ? '#00ff41' : down ? '#ff5555' : 'inherit', fontFamily: 'monospace' }}
                          >
                            {up ? '▲' : '▼'}{Math.abs(r.delta)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" fontFamily="monospace">{r.best_position}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" fontFamily="monospace" sx={{ opacity: 0.7 }}>
                          {r.avg_position ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" fontFamily="monospace" sx={{ opacity: 0.7 }}>
                          {r.days_seen}{totalDays ? `/${totalDays}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <RankSparkline pts={r.history} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(r.title || '')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!otherDomains?.domains || otherDomains.domains.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="caption" color="text.secondary">
                        Either everything ranking is already tracked OR no data yet for this window.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Panel>
    </Stack>
  );
};

export default CompetitorSerpPanel;
